// Product-approved outlier axis cap for the Account Overview activity chart
// (ENG-5256). When one bar dwarfs the rest, an uncapped Y-axis flattens every
// other bar into an unreadable sliver. This caps the axis just above the tallest
// "normal" bar so the everyday bars stay legible; spike bars then overrun the top
// and carry a value label with their TRUE value (rendered by the chart).
//
// Rule (confirmed):
//   nonZero   = values that are non-null and > 0
//   median    = median of nonZero
//   T         = 5 * median               (spike threshold)
//   spike     = value > T
//   if ≥1 spike: ceiling = niceCeil(1.2 * tallest non-spike bar), capped = true
//   else (no spike, or < 2 non-zero, or all values equal): no cap (auto behavior)

export interface AxisCap {
  /** True when the axis was capped because of at least one spike. */
  capped: boolean;
  /** Y-axis domain max. Only meaningful when `capped` is true. */
  ceiling: number;
  /** The spike threshold (5 × median). 0 when no cap applies. */
  threshold: number;
}

const NO_CAP: AxisCap = { capped: false, ceiling: 0, threshold: 0 };

function median(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Rounds a raw ceiling up to a readable "nice" number so the axis max isn't an
 * awkward value like 13.2. Picks a step from the {1, 2, 5} × 10ⁿ family sized to
 * the value's magnitude and rounds up to the next multiple of it.
 */
function niceCeil(value: number): number {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const niceStep = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = niceStep * magnitude;
  return Math.ceil(value / step) * step;
}

/**
 * Computes whether a series has an extreme outlier and, if so, the capped Y-axis
 * ceiling. Pure — no rounding of the input values, no side effects.
 */
export function computeAxisCap(values: (number | null)[]): AxisCap {
  const nonZero = values.filter((v): v is number => v !== null && v > 0);

  // Need at least two non-zero bars for an outlier to be meaningful.
  if (nonZero.length < 2) return NO_CAP;

  // All-equal series can't have a spike — nothing to cap.
  if (nonZero.every((v) => v === nonZero[0])) return NO_CAP;

  const sorted = [...nonZero].sort((a, b) => a - b);
  const threshold = 5 * median(sorted);

  const spikes = nonZero.filter((v) => v > threshold);
  if (spikes.length === 0) return NO_CAP;

  const nonSpikeMax = Math.max(...nonZero.filter((v) => v <= threshold));
  const ceiling = niceCeil(1.2 * nonSpikeMax);

  return { capped: true, ceiling, threshold };
}
